import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Loader2 } from "lucide-react";
import Wordmark from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/auth/AuthContext";
import { apiError } from "@/lib/api";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signup(email, password, name);
      toast.success("Account created");
      navigate("/onboarding");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "var(--slate-light)" }}
      data-testid="signup-page"
    >
      <header className="bg-white border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-6xl mx-auto px-6 py-5">
          <Link to="/">
            <Wordmark size={26} />
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div
          className="w-full max-w-md bg-white rounded-2xl border p-8 shadow-sm"
          style={{ borderColor: "var(--border)" }}
        >
          <h1 className="text-2xl font-bold mb-1" style={{ color: "var(--navy)" }}>
            Create your account
          </h1>
          <p className="text-sm mb-6" style={{ color: "var(--slate)" }}>
            Two minutes. Then create or join a scheme.
          </p>

          <Button
            variant="outline"
            className="w-full h-11 font-semibold mb-5"
            onClick={startGoogle}
            data-testid="signup-google-btn"
          >
            <span className="mr-2">G</span> Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
            <span className="text-[11px] uppercase tracking-wider" style={{ color: "var(--tagline-grey)" }}>
              or use email
            </span>
            <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>
                Full name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="signup-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="signup-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                data-testid="signup-password-input"
              />
              <p className="text-[11px]" style={{ color: "var(--tagline-grey)" }}>
                Min. 8 characters.
              </p>
            </div>
            {error && (
              <div
                className="rounded-md p-3 text-sm"
                style={{ background: "var(--alert-light)", color: "var(--alert)" }}
                data-testid="signup-error"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              className="w-full h-11 font-semibold"
              disabled={loading}
              style={{ background: "var(--blue)", color: "white" }}
              data-testid="signup-submit-btn"
            >
              {loading ? (<><Loader2 size={16} className="mr-1.5 animate-spin" /> Creating your account…</>) : (<><UserPlus size={16} className="mr-1.5" /> Create account</>)}
            </Button>
            {loading && (
              <p className="text-xs text-center mt-2 text-slate-600" data-testid="signup-loading-hint">
                This can take a few seconds. Please don&apos;t refresh.
              </p>
            )}
          </form>

          <p className="text-sm mt-6 text-center" style={{ color: "var(--slate)" }}>
            Already have an account?{" "}
            <Link to="/login" className="font-semibold" style={{ color: "var(--blue)" }} data-testid="signup-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
